import type { NightActionKind, RoleId, Winner } from '../roles';
import type { GameSettings } from '../settings';
import type { ScoreEntry } from '../scoring';

/** Псевдо-кандидат «пропустить голосование» (место 0). */
export const SKIP_VOTE = 0;
/** Ночной голос мафии «не убивать» (место 0). */
export const NO_KILL = 0;

export type Phase =
  | 'ROLE_REVEAL'
  | 'NIGHT'
  | 'MORNING'
  | 'DAY_SPEECH'
  /** Общее обсуждение после речей, перед голосованием. */
  | 'DISCUSSION'
  | 'VOTING'
  | 'TIE_SPEECH'
  | 'LAST_WORD'
  | 'GAME_OVER';

export type DeathReason = 'NIGHT_KILL' | 'VOTE' | 'LEFT';

export type MorningStep = 'KILL' | 'LOVER';

export interface PlayerState {
  seat: number;
  userId: string;
  nickname: string;
  role: RoleId;
  alive: boolean;
  connected: boolean;
  /** Игрок покинул игру насовсем (кнопка «Выйти» или долгий дисконнект). */
  left: boolean;
  deathReason: DeathReason | null;
  deathDay: number | null;
}

export interface CheckRecord {
  day: number;
  target: number;
  /** SHERIFF_CHECK: цель — мафия; DON_CHECK: цель — шериф. */
  positive: boolean;
}

export interface NightState {
  /** seat мафиози -> seat цели (NO_KILL — «не убивать»). Можно менять до конца ночи. */
  mafiaVotes: Record<number, number>;
  heal: number | null;
  maniacKill: number | null;
  /** Проверки фиксируются сразу (результат уже показан игроку), изменить нельзя. */
  sheriffCheck: number | null;
  donCheck: number | null;
  /** К кому пришла любовница. Можно менять до конца ночи. */
  loverVisit: number | null;
}

export interface SpeechState {
  order: number[];
  index: number;
  /** Что делать после последней речи: голосование/переголосование или следующая фаза. */
  then: 'VOTING' | 'REVOTE' | 'DAY_SPEECH' | 'NIGHT';
}

export interface VotingState {
  round: 1 | 2;
  candidates: number[];
  /** Можно ли голосовать за пропуск (SKIP_VOTE). */
  canSkip: boolean;
  /** seat голосующего -> seat кандидата (или SKIP_VOTE). Можно менять, пока голосование открыто. */
  votes: Record<number, number>;
}

export interface GameState {
  id: string;
  /** Монотонно растёт на каждом изменении — клиент отбрасывает устаревшие снапшоты. */
  version: number;
  /** Растёт на каждой смене фазы. Таймер несёт token, чтобы «просроченный» таймер не сработал в новой фазе. */
  phaseToken: number;
  phase: Phase;
  /** Номер дня; ночь N предшествует дню N. */
  day: number;
  phaseEndsAt: number | null;
  /** Начало текущей фазы/речи — для секундомера в ручном режиме. */
  phaseStartedAt: number;
  settings: GameSettings;
  players: PlayerState[];
  /** Ведущий без роли (пользователь), если игра идёт с ведущим. */
  moderatorUserId: string | null;

  roleAcks: number[];
  night: NightState;
  lastHealTarget: number | null;
  selfHealsUsed: number;
  sheriffChecks: CheckRecord[];
  donChecks: CheckRecord[];
  /** Кто погиб прошедшей ночью — публично, без указания убийцы. */
  lastNightDeaths: number[];
  /** С кем была любовница прошедшей ночью — ведущий объявляет это утром. */
  lastNightLoverVisit: number | null;
  /** Игрок, проведший ночь с любовницей: сегодня молчит и не голосует. */
  silenced: number | null;
  /** Гость, погибший вместе с любовницей (мафия пришла к ней), — объясняется утром. */
  lastNightWithLover: number | null;
  /**
   * Что показывает анимация утра: итоги ночи (KILL), затем визит любовницы (LOVER, если он был).
   * Шаг переключает таймер утра или ведущий — так длительность анимации в руках ведущего.
   */
  morningStep: MorningStep | null;

  speech: SpeechState | null;
  /** seat выставившего -> seat кандидата (порядок вставки = порядок выставления). */
  nominations: Record<number, number>;
  nominationOrder: number[];
  voting: VotingState | null;
  /** Участники переголосования (может включать SKIP_VOTE). */
  tieCandidates: number[];
  /** Пропуск голосования уже использован в этой игре. */
  skipUsed: boolean;
  lastVote: { eliminated: number | null; tally: Record<number, number>; skipped: boolean } | null;

  /** Журнал баллов. Раскрывает роли — игрокам отдаётся только после конца игры. */
  scores: ScoreEntry[];
  /** До какого дня включительно уже начислены баллы «мирный пережил раунд». */
  lastRoundScored: number;

  winner: Winner | null;
  /** Порядковый номер следующей записи журнала. */
  logSeq: number;
}

// ---------- Входящие команды (от игроков и от таймера) ----------

export type GameCommand =
  | { type: 'ACK_ROLE'; seat: number }
  | { type: 'NIGHT_ACTION'; seat: number; action: NightActionKind; target: number }
  | { type: 'PASS_SPEECH'; seat: number }
  | { type: 'NOMINATE'; seat: number; target: number }
  | { type: 'VOTE'; seat: number; target: number }
  | { type: 'LEAVE'; seat: number; kicked?: boolean }
  | { type: 'SET_CONNECTED'; seat: number; connected: boolean }
  | { type: 'TIMEOUT'; token: number }
  /**
   * Ведущий переключает фазу / спикера вручную (то же, что истечение таймера).
   * token — phaseToken, который видел ведущий: повторный клик после смены фазы игнорируется.
   */
  | { type: 'ADVANCE'; token?: number };

export type GameErrorCode =
  | 'WRONG_PHASE'
  | 'NOT_A_PLAYER'
  | 'PLAYER_DEAD'
  | 'NOT_YOUR_TURN'
  | 'ACTION_NOT_ALLOWED'
  | 'INVALID_TARGET'
  | 'ALREADY_DONE'
  | 'SILENCED'
  | 'STALE_TIMER'
  /** Ведущий уже переключил фазу — команда относилась к предыдущей. */
  | 'STALE_PHASE'
  /** В ручном режиме слово передаёт только ведущий. */
  | 'MODERATOR_LEADS';

// ---------- Исходящие эффекты (исполняет GameRunner) ----------

export const LOG_TYPES = [
  'GAME_STARTED',
  'ROLE_ASSIGNED',
  'PHASE_STARTED',
  'MAFIA_VOTE',
  'MAFIA_MISS',
  'DON_CHECK',
  'SHERIFF_CHECK',
  'DOCTOR_HEAL',
  'MANIAC_TARGET',
  'KILLED',
  'SAVED',
  'NOMINATED',
  'VOTE_CAST',
  'VOTE_RESULT',
  'ELIMINATED',
  'PLAYER_LEFT',
  'PLAYER_DISCONNECTED',
  'PLAYER_RECONNECTED',
  'GAME_ENDED',
  'LOVER_VISIT',
  'VOTE_SKIPPED',
  'MODERATOR_ADVANCE',
  'SCORE',
] as const;
export type LogType = (typeof LOG_TYPES)[number];

export interface LogEntry {
  seq: number;
  at: number;
  day: number;
  phase: Phase;
  type: LogType;
  actorSeat: number | null;
  targetSeat: number | null;
  payload: Record<string, unknown> | null;
}

export type Audience = { kind: 'all' } | { kind: 'seat'; seat: number } | { kind: 'mafia' };

/** Разовые уведомления для тостов/анимаций. Полное состояние клиент всё равно получает снапшотом. */
export type GameNotice =
  | { type: 'PHASE_CHANGED'; phase: Phase; day: number }
  | { type: 'NIGHT_RESULT'; deaths: number[]; loverVisit: number | null; withLover: number | null }
  | { type: 'CHECK_RESULT'; action: 'SHERIFF_CHECK' | 'DON_CHECK'; target: number; positive: boolean }
  | { type: 'VOTE_RESULT'; eliminated: number | null; tie: boolean; skipped: boolean; tally: Record<number, number> }
  | { type: 'PLAYER_LEFT'; seat: number }
  | { type: 'GAME_OVER'; winner: Winner };

export type Effect =
  | { type: 'LOG'; entry: LogEntry }
  | { type: 'NOTIFY'; to: Audience; notice: GameNotice };

export type ReduceResult =
  | { ok: true; state: GameState; effects: Effect[] }
  | { ok: false; error: GameErrorCode };
